import { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { isLlmInferenceAvailable } from '../lib/aiCapability';
import { MODEL_OPTIONS } from '../lib/modelCatalog';
import { playSpeech, stopSpeech, XAI_SPEECH_VOICES } from '../lib/speechPlayback';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { AgentToolRail, LectureAiChip, SurfaceHero } from '../components/design-kit';
import { 
  ModePickerMenu, 
  ActiveModeIndicator, 
  detectModesFromText, 
  isModeCommand,
  buildModeSystemPrompt,
  LEARN_ASSIST_MODES,
  blendModeColors
} from '../components/LearnAssistModes';
import { toast } from 'sonner';
import { 
  Mic, 
  MicOff,
  Send,
  ChevronLeft,
  Loader2,
  MessageSquare,
  Volume2,
  VolumeX,
  AlertCircle,
  User,
  Bot,
  Upload,
  Link as LinkIcon,
  X,
  FileText,
  Globe,
  GraduationCap,
  BookOpen,
  Calendar,
  Clock,
  ChevronDown,
  Check,
  Sparkles,
  Zap
} from 'lucide-react';

const getLatestMessageContent = (messages, role) =>
  [...messages].reverse().find((message) => message.role === role && message.content)?.content || '';

function formatAgentToolMessage(tool, result) {
  const data = result?.data || {};

  if (tool.id === 'search_memory') {
    const matches = data.matches || [];
    if (!matches.length) {
      return 'I searched your saved memory but did not find a relevant match yet.';
    }
    const lines = matches.slice(0, 3).map((match, index) => {
      const title = match.metadata?.title || match.metadata?.topic || match.source_type;
      return `${index + 1}. ${title}: ${match.content_text?.slice(0, 180) || ''}`;
    });
    return `Memory search is ready:\n${lines.join('\n')}`;
  }

  if (tool.id === 'review_weak_areas') {
    const focusAreas = data.focus_areas || [];
    const summary = data.cracked_score || {};
    const parts = [
      `Accuracy: ${summary.review_accuracy_pct ?? 'n/a'}%`,
      `Open confusions: ${data.confusion_backlog?.open_count ?? 0}`,
      `Reviews in progress: ${data.pending_tasks?.reviews_in_progress?.length ?? 0}`,
    ];
    if (!focusAreas.length) {
      return `Weak-area review complete. ${parts.join(' • ')}`;
    }
    const focusText = focusAreas
      .map((area) => `${area.name} (${area.confusion_open} open, ${area.review_accuracy_pct ?? 'n/a'}% accuracy)`)
      .join('\n');
    return `Weak-area review complete. ${parts.join(' • ')}\n${focusText}`;
  }

  if (tool.id === 'attach_library_context') {
    const document = data.document;
    if (!document) {
      return result?.message || 'No library context was attached.';
    }
    return `Attached "${document.title}" from your library and added it to this conversation.`;
  }

  if (tool.id === 'save_note_summary') {
    const note = data.note;
    return note ? `Saved "${note.title}" to Notes.` : (result?.message || 'Saved to notes.');
  }

  if (tool.id === 'create_revisit_task') {
    return result?.message || 'Created a revisit task.';
  }

  if (tool.id === 'log_confusion_point') {
    return result?.message || 'Logged a confusion point.';
  }

  return result?.message || `${tool.label} completed.`;
}

export default function QAPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get URL parameters for Learn-assist pre-fill
  const urlTab = searchParams.get('tab');
  const urlTopic = searchParams.get('topic');
  const urlTool = searchParams.get('tool');
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(user?.preferred_model || 'gpt-5.4');
  const [assistantMode, setAssistantMode] = useState('tutor');
  
  // Voice state
  const [voiceMode, setVoiceMode] = useState(user?.voice_mode || 'browser');
  const [speechProvider, setSpeechProvider] = useState(() => localStorage.getItem('qa_speech_provider') || 'browser');
  const [xaiVoiceId, setXaiVoiceId] = useState(() => localStorage.getItem('qa_xai_voice') || 'eve');
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakResponses, setSpeakResponses] = useState(true);
  const [interimTranscript, setInterimTranscript] = useState('');
  
  // Context state
  const [contextDocuments, setContextDocuments] = useState([]);
  const [contextUrls, setContextUrls] = useState([]);
  const [urlInput, setUrlInput] = useState('');
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);
  const [showContextPanel, setShowContextPanel] = useState(false);
  const [libraryDocuments, setLibraryDocuments] = useState([]);
  const [agentTools, setAgentTools] = useState([]);
  
  // Refs
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const speechAudioRef = useRef(null);

  // Lectures for context - now supports multiple selection
  const [lectures, setLectures] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedLectures, setSelectedLectures] = useState([]); // Array of lecture IDs
  const [lecturePickerOpen, setLecturePickerOpen] = useState(false);
  
  // Modes are now inline - no more separate tab needed
  
  // Learn-assist MODES state (new conversational mode system)
  const [activeModes, setActiveModes] = useState([]);
  
  // Pre-filled topic from URL (e.g., from Review page "Strengthen this topic")
  const [prefilledTopic, setPrefilledTopic] = useState(urlTopic || '');
  const [prefilledTool, setPrefilledTool] = useState(urlTool || '');
  
  // Handle URL pre-fill for modes
  useEffect(() => {
    if (urlTool && LEARN_ASSIST_MODES[urlTool]) {
      setActiveModes([urlTool]);
    }
  }, [urlTool]);
  
  // Handle URL parameter pre-fill
  useEffect(() => {
    if (urlTopic) {
      setPrefilledTopic(urlTopic);
    }
    if (urlTool) {
      setPrefilledTool(urlTool);
    }
    // Clear URL params after reading
    if (urlTab || urlTopic || urlTool) {
      setSearchParams({});
    }
  }, [urlTab, urlTopic, urlTool, setSearchParams]);

  useEffect(() => {
    loadLectures();
    loadSubjects();
    loadLibraryDocuments();
    loadAgentTools();
    setupSpeechRecognition();
    const synth = synthRef.current;
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      synth?.cancel();
      if (speechAudioRef.current) {
        speechAudioRef.current.pause();
        if (speechAudioRef.current.src) {
          URL.revokeObjectURL(speechAudioRef.current.src);
        }
        speechAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('qa_speech_provider', speechProvider);
  }, [speechProvider]);

  useEffect(() => {
    localStorage.setItem('qa_xai_voice', xaiVoiceId);
  }, [xaiVoiceId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadLectures = async () => {
    try {
      const data = await api.getLectures();
      setLectures(data.filter(l => l.status === 'completed'));
    } catch (error) {
      console.error('Failed to load lectures:', error);
    }
  };

  const loadSubjects = async () => {
    try {
      const data = await api.getSubjects();
      setSubjects(data);
    } catch (error) {
      console.error('Failed to load subjects:', error);
    }
  };

  const loadLibraryDocuments = async () => {
    try {
      const data = await api.getDocuments();
      setLibraryDocuments(data.documents || []);
    } catch (error) {
      console.error('Failed to load library documents:', error);
    }
  };

  const loadAgentTools = async () => {
    try {
      const data = await api.getAgentTools('chat');
      setAgentTools(data.tools || []);
    } catch (error) {
      console.error('Failed to load agent tools:', error);
    }
  };

  // Get subject info for a lecture
  const getSubjectForLecture = (lecture) => {
    if (!lecture.subject_id) return null;
    return subjects.find(s => s.id === lecture.subject_id);
  };

  // Toggle lecture selection
  const toggleLectureSelection = (lectureId) => {
    setSelectedLectures(prev => {
      if (prev.includes(lectureId)) {
        return prev.filter(id => id !== lectureId);
      }
      return [...prev, lectureId];
    });
  };

  // Get selected lectures data
  const getSelectedLecturesData = () => {
    return lectures.filter(l => selectedLectures.includes(l.id));
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Format duration for display
  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    return `${mins} min`;
  };

  const setupSpeechRecognition = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.warn('Speech recognition not supported');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }
      
      setInterimTranscript(interim);
      if (final) {
        setInputText(prev => prev + final);
        setInterimTranscript('');
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error !== 'no-speech') {
        toast.error(`Speech recognition error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript('');
    };

    recognitionRef.current = recognition;
  };

  const toggleListening = () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported in your browser');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
      toast.success('Listening... Speak now');
    }
  };

  const startWhisperRecording = async () => {
    if (!hasApiKey) {
      toast.error('Please add your OpenAI API key in settings');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Transcribe with Whisper
        try {
          setIsLoading(true);
          const result = await api.transcribe(blob);
          setInputText(result.text);
          toast.success('Transcription complete');
        } catch (error) {
          toast.error(error.message || 'Transcription failed');
        } finally {
          setIsLoading(false);
        }
      };

      mediaRecorder.start();
      setIsListening(true);
      toast.success('Recording... Click again to stop');
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Failed to start recording');
    }
  };

  const stopWhisperRecording = () => {
    if (mediaRecorderRef.current && isListening) {
      mediaRecorderRef.current.stop();
      setIsListening(false);
    }
  };

  const handleVoiceInput = () => {
    if (voiceMode === 'whisper') {
      if (isListening) {
        stopWhisperRecording();
      } else {
        startWhisperRecording();
      }
    } else {
      toggleListening();
    }
  };

  const hasApiKey = isLlmInferenceAvailable(user);
  const hasXaiSpeech = Boolean(user?.has_xai_api_key || user?.platform_xai_configured);
  const hasContext = contextDocuments.length > 0 || contextUrls.length > 0 || selectedLectures.length > 0;

  const speakText = async (text) => {
    if (!speakResponses || !text?.trim()) return;
    if (speechProvider === 'xai' && !hasXaiSpeech) {
      toast.error('Add an xAI key in settings to use Grok speech');
      return;
    }
    try {
      await playSpeech({
        text,
        provider: speechProvider,
        xaiVoiceId,
        synth: synthRef.current,
        audioRef: speechAudioRef,
        onStart: () => setIsSpeaking(true),
        onEnd: () => setIsSpeaking(false),
      });
    } catch (error) {
      console.error('Speech playback failed:', error);
      setIsSpeaking(false);
      toast.error(error.message || 'Failed to play speech');
    }
  };

  const stopSpeaking = () => {
    stopSpeech({
      provider: speechProvider,
      synth: synthRef.current,
      audioRef: speechAudioRef,
      onEnd: () => setIsSpeaking(false),
    });
  };

  // Document upload handler
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploadingDoc(true);
    try {
      const createResult = await api.createDocument({
        title: file.name,
        original_filename: file.name,
        mime_type: file.type || 'application/octet-stream'
      });

      let documentRecord = createResult.document;
      let extractedContent = '';

      if (createResult.upload?.url) {
        const uploadResponse = await fetch(createResult.upload.url, {
          method: createResult.upload.method || 'PUT',
          headers: {
            'Content-Type': file.type || 'application/octet-stream'
          },
          body: file
        });
        if (!uploadResponse.ok) {
          throw new Error('Storage upload failed');
        }
        const complete = await api.completeDocumentUpload(documentRecord.id, {
          byte_size: file.size,
          wait_for_normalization: true,
        });
        documentRecord = complete.document;
        if (complete.normalization_error) {
          throw new Error(complete.normalization_error);
        }
        const extracted = await api.extractDocumentText(documentRecord.id);
        extractedContent = extracted.content;
      } else {
        const parsed = await api.parseDocument(file);
        extractedContent = parsed.content;
        await api.deleteDocument(documentRecord.id);
        const textDoc = await api.createTextDocument({
          title: file.name,
          original_filename: file.name,
          mime_type: file.type || 'text/plain',
          content: parsed.content
        });
        documentRecord = textDoc.document;
      }

      setContextDocuments(prev => [
        ...prev,
        { name: documentRecord.title || file.name, content: extractedContent, documentId: documentRecord.id }
      ]);
      await loadLibraryDocuments();
      toast.success(`Document "${file.name}" saved to Library and added to context`);
    } catch (error) {
      toast.error(error.message || 'Failed to save document');
    } finally {
      setIsUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // URL fetch handler
  const handleAddUrl = async () => {
    if (!urlInput.trim()) return;
    
    // Basic URL validation
    let url = urlInput.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    setIsFetchingUrl(true);
    try {
      const result = await api.fetchUrl(url);
      const saved = await api.createTextDocument({
        title: new URL(result.url).hostname,
        original_filename: `${new URL(result.url).hostname}.url.txt`,
        mime_type: 'text/url',
        content: result.content,
        source_url: result.url
      });
      setContextUrls(prev => [...prev, { url: result.url, content: result.content, documentId: saved.document.id }]);
      setUrlInput('');
      await loadLibraryDocuments();
      toast.success(`Link saved to Library and added to context`);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch URL content');
    } finally {
      setIsFetchingUrl(false);
    }
  };

  const attachLibraryDocument = async (document) => {
    try {
      if (contextDocuments.some((item) => item.documentId === document.id)) {
        return;
      }
      const extracted = await api.extractDocumentText(document.id);
      setContextDocuments(prev => [
        ...prev,
        { name: document.title, content: extracted.content, documentId: document.id }
      ]);
      toast.success(`Attached "${document.title}" from Library`);
    } catch (error) {
      toast.error(error.message || 'Failed to attach library document');
    }
  };

  const applyAgentTool = async (tool) => {
    if (!tool) return;

    const trimmedInput = inputText.trim();
    const latestUserMessage = getLatestMessageContent(messages, 'user');
    const latestAssistantMessage = getLatestMessageContent(messages, 'assistant');
    const activeLecture = lectures.find((lecture) => lecture.id === selectedLectures[selectedLectures.length - 1]);
    const inferredSubjectId = activeLecture?.subject_id || null;

    if (tool.id === 'search_memory' && !trimmedInput && !latestUserMessage) {
      setInputText(tool.prompt_hint || '');
      toast.info('Type a question first, then run memory search.');
      return;
    }

    if ((tool.id === 'log_confusion_point' || tool.id === 'create_revisit_task') && !trimmedInput && !latestUserMessage) {
      setInputText(tool.prompt_hint || '');
      toast.info('Add the topic you want to save first.');
      return;
    }

    if (tool.id === 'save_note_summary' && !latestAssistantMessage && !trimmedInput) {
      setInputText(tool.prompt_hint || '');
      toast.info('Ask for an explanation first so I have something to save.');
      return;
    }

    try {
      const payload = {
        subject_id: inferredSubjectId,
        lecture_id: activeLecture?.id || null,
      };

      if (tool.id === 'search_memory') {
        payload.query = trimmedInput || latestUserMessage;
      }

      if (tool.id === 'log_confusion_point') {
        payload.confusion_text = trimmedInput || latestUserMessage;
        payload.source = 'qa_agent_tool';
      }

      if (tool.id === 'create_revisit_task') {
        payload.topic = trimmedInput || latestUserMessage;
        payload.notes = latestAssistantMessage || null;
        payload.source = 'qa_agent_tool';
      }

      if (tool.id === 'attach_library_context') {
        const recentLibraryItem = libraryDocuments.find((document) => document.has_extracted_text)
          || libraryDocuments[0];
        if (recentLibraryItem) {
          payload.document_id = recentLibraryItem.id;
        }
        payload.query = trimmedInput || latestUserMessage || '';
      }

      if (tool.id === 'save_note_summary') {
        payload.content = latestAssistantMessage || trimmedInput;
        payload.title = `${assistantMode === 'reviewer' ? 'Coach' : 'Tutor'} summary`;
      }

      const response = await api.executeAgentTool(tool.id, payload, 'chat');
      const result = response.result;

      if (tool.id === 'attach_library_context' && result?.data?.document) {
        const document = result.data.document;
        setContextDocuments((prev) => {
          if (prev.some((item) => item.documentId === document.id)) {
            return prev;
          }
          return [
            ...prev,
            { name: document.title, content: document.content, documentId: document.id }
          ];
        });
      }

      if (tool.id === 'save_note_summary' && result?.data?.note?.id) {
        toast.success('Saved to Notes');
      } else {
        toast.success(result?.message || `${tool.label} complete`);
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: formatAgentToolMessage(tool, result),
          isSystemMessage: true,
          toolResult: response,
        }
      ]);
    } catch (error) {
      console.error('Agent tool failed:', error);
      toast.error(error.message || `Failed to run ${tool.label}`);
    }
  };

  const removeDocument = (index) => {
    setContextDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const removeUrl = (index) => {
    setContextUrls(prev => prev.filter((_, i) => i !== index));
  };

  // Toggle a mode on/off
  const toggleMode = (modeId) => {
    setActiveModes(prev => {
      if (prev.includes(modeId)) {
        return prev.filter(id => id !== modeId);
      } else {
        return [...prev, modeId];
      }
    });
  };

  // Clear all active modes
  const clearModes = () => {
    setActiveModes([]);
  };

  const sendMessage = async () => {
    const text = inputText.trim();
    if (!text || isLoading) return;

    if (!hasApiKey) {
      toast.error('Please add your OpenAI API key in settings');
      return;
    }

    // Check for exit/clear mode commands
    const exitCommands = ['exit mode', 'exit modes', 'clear mode', 'clear modes', 'normal mode', 'regular mode', 'disable mode', 'disable modes'];
    const lowerText = text.toLowerCase();
    if (exitCommands.some(cmd => lowerText.includes(cmd)) && activeModes.length > 0) {
      const clearedModes = activeModes.map(id => LEARN_ASSIST_MODES[id]?.name).filter(Boolean);
      setActiveModes([]);
      const systemMessage = {
        role: 'assistant',
        content: `✓ All modes deactivated (${clearedModes.join(', ')}). I'm back to normal assistant mode. How can I help?`,
        isSystemMessage: true
      };
      setMessages(prev => [...prev, systemMessage]);
      setInputText('');
      return;
    }

    // Check for mode commands in the text
    const detectedModes = detectModesFromText(text);
    const isOnlyModeCommand = isModeCommand(text);
    
    // If user just typed a mode command, activate it and show confirmation
    if (isOnlyModeCommand && detectedModes.length > 0) {
      setActiveModes(prev => {
        const newModes = [...new Set([...prev, ...detectedModes])];
        return newModes;
      });
      
      const modeNames = detectedModes.map(id => LEARN_ASSIST_MODES[id]?.name).filter(Boolean);
      const systemMessage = { 
        role: 'assistant', 
        content: `✨ **${modeNames.join(' + ')} Mode activated!**\n\nI'm now using ${modeNames.length > 1 ? 'these methodologies' : 'this methodology'} to help you learn. What would you like to explore?`,
        isSystemMessage: true,
        activatedModes: detectedModes
      };
      setMessages(prev => [...prev, systemMessage]);
      setInputText('');
      return;
    }

    // If modes were mentioned in a longer message, activate them silently
    if (detectedModes.length > 0) {
      setActiveModes(prev => {
        const newModes = [...new Set([...prev, ...detectedModes])];
        return newModes;
      });
    }

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      // Build messages with mode-aware system prompt
      const baseSystemPrompt = `${assistantMode === 'reviewer'
        ? 'You are LectureAI in honest coach mode. Be direct about weak understanding, false confidence, and what the student should study next.'
        : 'You are a helpful AI tutor assistant. Help the student understand lecture material, answer questions, and explain concepts clearly.'}

IMPORTANT: Format your responses as clean plain text. Do NOT use markdown formatting like ** for bold, ## for headers, or * for bullet points. Use plain text, numbers, and dashes instead.`;

      // Combine with active modes
      const currentModes = [...new Set([...activeModes, ...detectedModes])];
      const systemPrompt = buildModeSystemPrompt(currentModes, baseSystemPrompt);

      const allMessages = [
        { role: 'system', content: systemPrompt },
        ...messages.filter(m => !m.isSystemMessage).map(m => ({
          role: m.role,
          content: m.content
        })),
        { role: 'user', content: text }
      ];

      // Prepare context
      const docContents = [
        ...contextDocuments.map(d => d.content),
        ...contextUrls.map(u => u.content).filter(Boolean)
      ];
      const urlContents = contextUrls.map(u => u.url);

      // Use multiple lectures if selected
      const result = await api.chat(
        allMessages, 
        selectedModel, 
        null, // No single lecture
        docContents,
        urlContents,
        selectedLectures, // Multiple lectures
        assistantMode
      );
      
      const assistantMessage = { role: 'assistant', content: result.response };
      setMessages(prev => [...prev, assistantMessage]);
      
      // Speak the response
      speakText(result.response);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error(error.message || 'Failed to get response');
    } finally {
      setIsLoading(false);
    }
  };


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const createReviewNote = async () => {
    const conversationMessages = messages
      .filter((message) => !message.isSystemMessage && message.content)
      .slice(-20)
      .map((message) => ({ role: message.role, content: message.content }));
    if (!conversationMessages.length) {
      toast.info('Have a short conversation first, then turn it into a review note.');
      return;
    }
    const selectedLectureData = getSelectedLecturesData();
    const subjectId = selectedLectureData[0]?.subject_id || null;
    try {
      const result = await api.createReviewNote({
        title: `${assistantMode === 'reviewer' ? 'Honest coach' : 'QnA'} review note`,
        source_surface: 'qa',
        subject_id: subjectId,
        lecture_ids: selectedLectures,
        document_ids: contextDocuments.map((document) => document.documentId).filter(Boolean),
        messages: conversationMessages,
        metadata: {
          assistant_mode: assistantMode,
          selected_model: selectedModel,
          context_url_count: contextUrls.length,
        },
      });
      toast.success(`Saved "${result.note?.title || 'review note'}" to Notes`);
    } catch (error) {
      toast.error(error.message || 'Failed to create review note');
    }
  };
  
  // Get combined lecture context for Learn-assist
  const getLectureContextForLearnAssist = () => {
    const selectedLectureData = getSelectedLecturesData();
    if (selectedLectureData.length === 0) return '';
    return selectedLectureData
      .map(l => l.formatted_notes || '')
      .join('\n\n---\n\n');
  };

  // Generate gradient based on active modes
  const getBackgroundGradient = () => {
    if (activeModes.length === 0) {
      // Default subtle gradient
      return 'from-slate-100 via-blue-50/30 to-indigo-50/30 dark:from-slate-900 dark:via-blue-950/30 dark:to-indigo-950/30';
    }
    
    // Blend mode colors for background
    const modeGradients = {
      'first-principles': 'from-violet-100/50 via-purple-50/40 to-indigo-100/50 dark:from-violet-950/30 dark:via-purple-950/20 dark:to-indigo-950/30',
      'fast-context': 'from-cyan-100/50 via-blue-50/40 to-indigo-100/50 dark:from-cyan-950/30 dark:via-blue-950/20 dark:to-indigo-950/30',
      'feynman': 'from-emerald-100/50 via-green-50/40 to-teal-100/50 dark:from-emerald-950/30 dark:via-green-950/20 dark:to-teal-950/30',
      'examples': 'from-amber-100/50 via-orange-50/40 to-red-100/50 dark:from-amber-950/30 dark:via-orange-950/20 dark:to-red-950/30',
      'memory': 'from-pink-100/50 via-rose-50/40 to-red-100/50 dark:from-pink-950/30 dark:via-rose-950/20 dark:to-red-950/30',
      'quiz': 'from-indigo-100/50 via-violet-50/40 to-purple-100/50 dark:from-indigo-950/30 dark:via-violet-950/20 dark:to-purple-950/30',
      'plan': 'from-teal-100/50 via-emerald-50/40 to-green-100/50 dark:from-teal-950/30 dark:via-emerald-950/20 dark:to-green-950/30'
    };
    
    // Get first mode's gradient (could blend multiple but keep it simple)
    const firstMode = activeModes[0];
    return modeGradients[firstMode] || modeGradients['first-principles'];
  };

  return (
    <div className={`min-h-screen flex flex-col relative overflow-hidden`}>
      {/* Animated gradient background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${getBackgroundGradient()} transition-all duration-1000 ease-in-out`} />
      
      {/* Decorative gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute -top-1/4 -right-1/4 w-1/2 h-1/2 rounded-full blur-3xl opacity-30 transition-all duration-1000 ${
          activeModes.length > 0 
            ? activeModes.includes('examples') ? 'bg-orange-400' 
              : activeModes.includes('feynman') ? 'bg-green-400'
              : activeModes.includes('quiz') ? 'bg-indigo-400'
              : activeModes.includes('memory') ? 'bg-pink-400'
              : 'bg-violet-400'
            : 'bg-blue-400'
        }`} />
        <div className={`absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 rounded-full blur-3xl opacity-20 transition-all duration-1000 ${
          activeModes.length > 0 
            ? activeModes.includes('fast-context') ? 'bg-cyan-400'
              : activeModes.includes('plan') ? 'bg-teal-400'
              : activeModes.includes('first-principles') ? 'bg-purple-400'
              : 'bg-indigo-400'
            : 'bg-indigo-400'
        }`} />
      </div>
      
      {/* Content layer */}
      <div className="relative z-10 flex flex-col min-h-screen">
      {/* Header */}
      <header className="glass sticky top-0 z-50 backdrop-blur-xl bg-background/80">
        <div className="mx-auto max-w-5xl px-4 py-3 sm:px-6 sm:py-4">
          <SurfaceHero
            eyebrow="Ask"
            title="Memory-backed tutoring on demand"
            detail="Ask against lectures, saved library items, and indexed memory. Switch to Honest Coach when you want sharper feedback instead of reassurance."
            aside={(
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" size="icon">
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                </Link>
                <Button
                  variant={showContextPanel ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowContextPanel(!showContextPanel)}
                  className="rounded-xl"
                  data-testid="toggle-context-btn"
                >
                  {hasContext && <span className="mr-2 h-2 w-2 rounded-full bg-green-500" />}
                  Context
                  {selectedLectures.length > 0 && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {selectedLectures.length}
                    </Badge>
                  )}
                </Button>
              </>
            )}
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto flex w-full max-w-5xl flex-1 px-0 py-3 sm:px-4 sm:py-4">
        {/* Main Area - Chat with glassmorphism */}
        <div className={`lectureai-surface flex flex-1 flex-col overflow-hidden rounded-xl border border-white/20 bg-background/70 shadow-2xl backdrop-blur-xl transition-all sm:rounded-2xl ${showContextPanel ? 'md:mr-80' : ''}`}>
          {/* Settings Bar */}
          <div className="p-4 border-b border-border/50 bg-background/50">
            <div className="flex flex-wrap items-center gap-3">
              {/* Model Selection */}
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">Model:</Label>
                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger className="w-40 h-9" data-testid="qa-model-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODEL_OPTIONS.map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">Assistant:</Label>
                <div className="inline-flex rounded-xl border border-border bg-background p-1">
                  <button
                    type="button"
                    onClick={() => setAssistantMode('tutor')}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                      assistantMode === 'tutor' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Tutor
                  </button>
                  <button
                    type="button"
                    onClick={() => setAssistantMode('reviewer')}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                      assistantMode === 'reviewer' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Honest Coach
                  </button>
                </div>
              </div>

              {/* Voice Mode */}
              <>
                  <Tabs value={voiceMode} onValueChange={setVoiceMode} className="w-full sm:ml-auto sm:w-auto">
                    <TabsList className="h-9">
                      <TabsTrigger value="browser" className="text-xs" data-testid="browser-voice-tab">
                        Browser Voice
                      </TabsTrigger>
                      <TabsTrigger value="whisper" className="text-xs" data-testid="whisper-voice-tab">
                        Whisper
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>

                  {/* Speak Responses Toggle */}
                  <div className="flex items-center gap-2">
                    <Switch
                      id="speak-responses"
                      checked={speakResponses}
                      onCheckedChange={setSpeakResponses}
                      data-testid="speak-responses-toggle"
                    />
                    <Label htmlFor="speak-responses" className="text-sm cursor-pointer">
                      {speakResponses ? (
                        <Volume2 className="w-4 h-4" />
                      ) : (
                        <VolumeX className="w-4 h-4 text-muted-foreground" />
                      )}
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground">Speech:</Label>
                    <Select value={speechProvider} onValueChange={setSpeechProvider}>
                      <SelectTrigger className="w-36 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="browser">Browser</SelectItem>
                        <SelectItem value="xai" disabled={!hasXaiSpeech}>
                          Grok {!hasXaiSpeech && '(Unavailable)'}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {speechProvider === 'xai' && (
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground">Grok voice:</Label>
                      <Select value={xaiVoiceId} onValueChange={setXaiVoiceId}>
                        <SelectTrigger className="w-36 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {XAI_SPEECH_VOICES.map((voice) => (
                            <SelectItem key={voice.id} value={voice.id}>
                              {voice.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}
              
              {/* Clear chat button when modes active */}
              {activeModes.length > 0 && messages.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setMessages([])}
                  className="rounded-xl sm:ml-auto"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear Conversation
                </Button>
              )}
              {messages.length > 1 && (
                <Button variant="outline" size="sm" onClick={createReviewNote} className="rounded-xl">
                  <FileText className="w-4 h-4 mr-2" />
                  Make Review Note
                </Button>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {activeModes.length > 0 ? (
                <LectureAiChip
                  label={`${activeModes.map(id => LEARN_ASSIST_MODES[id]?.shortName).join(' + ')} mode`}
                  tone="subject"
                  active
                />
              ) : null}
              <LectureAiChip
                label={assistantMode === 'reviewer' ? 'Honest Coach active' : 'Tutor active'}
                tone={assistantMode === 'reviewer' ? 'tool' : 'subject'}
                active
              />
            </div>
            <div className="mt-4">
              <AgentToolRail tools={agentTools} onToolClick={applyAgentTool} title="Allowlisted agent tools" />
            </div>
          </div>

          {/* API Key Warning */}
          {!hasApiKey && (
            <div className="p-4 bg-amber-500/10 border-b border-amber-500/20">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                <p className="text-sm">
                  Add your OpenAI API key in{' '}
                  <Link to="/settings" className="underline text-primary">settings</Link>{' '}
                  to use Q&A features.
                </p>
              </div>
            </div>
          )}

          {/* Chat Content */}
          <>
              {/* Messages */}
              <ScrollArea className="flex-1 p-3 sm:p-4">
                <div className="space-y-4 max-w-3xl mx-auto">
                  {messages.length === 0 ? (
                    <div className="text-center py-16">
                      <MessageSquare className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                      <h3 className="font-serif text-xl text-muted-foreground mb-2">
                        Start a Conversation
                      </h3>
                      <p className="text-muted-foreground/70 text-sm max-w-md mx-auto">
                        Ask questions using voice or text. Add context from documents, URLs, or lectures for more relevant answers.
                      </p>
                      {selectedLectures.length > 0 && (
                        <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                          <BookOpen className="w-4 h-4" />
                          {selectedLectures.length} lecture{selectedLectures.length !== 1 ? 's' : ''} selected as context
                        </div>
                      )}
                    </div>
                  ) : (
                    messages.map((message, index) => (
                      <div
                        key={index}
                        className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : ''}`}
                        data-testid={`message-${index}`}
                      >
                        {message.role === 'assistant' && (
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Bot className="w-4 h-4 text-primary" />
                          </div>
                        )}
                        <div
                          className={`max-w-[88%] rounded-2xl px-3 py-3 sm:max-w-[80%] sm:px-4 ${
                            message.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-card border border-border'
                          }`}
                        >
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">
                            {message.content}
                          </p>
                        </div>
                        {message.role === 'user' && (
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  
                  {isLoading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-primary" />
                      </div>
                      <div className="bg-card border border-border rounded-2xl px-4 py-3">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Input Area */}
              <div className="border-t border-white/10 bg-background/50 p-3 backdrop-blur-sm sm:p-4">
                <div className="max-w-3xl mx-auto">
                  {/* Interim Transcript */}
                  {interimTranscript && (
                    <div className="mb-2 text-sm text-muted-foreground italic">
                      {interimTranscript}
                    </div>
                  )}
                  
                  {/* Speaking Indicator */}
                  {isSpeaking && (
                    <div className="mb-2 flex items-center gap-2 text-sm text-primary">
                      <Volume2 className="w-4 h-4 animate-pulse" />
                      Speaking...
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={stopSpeaking}
                        className="h-6 px-2"
                      >
                        Stop
                      </Button>
                    </div>
                  )}

                  {/* Active Mode Indicator */}
                  <ActiveModeIndicator 
                    activeModes={activeModes}
                    onRemoveMode={(modeId) => setActiveModes(prev => prev.filter(id => id !== modeId))}
                    onClearAll={clearModes}
                  />

                  {/* Input Container with gradient border when modes active */}
                  <div className={`relative rounded-2xl ${
                    activeModes.length > 0 
                      ? `p-[2px] ${blendModeColors(activeModes)}`
                      : ''
                  }`}>
                    <div className={`flex gap-2 ${activeModes.length > 0 ? 'rounded-2xl bg-background p-1' : ''}`}>
                      {/* Mode Picker Button */}
                      <ModePickerMenu 
                        activeModes={activeModes}
                        onToggleMode={toggleMode}
                        onClearModes={clearModes}
                      />

                      {/* Voice Input Button */}
                      <Button
                        variant={isListening ? 'destructive' : 'outline'}
                        size="icon"
                        onClick={handleVoiceInput}
                        disabled={!hasApiKey && voiceMode === 'whisper'}
                        className={`flex-shrink-0 rounded-xl ${isListening ? 'recording-active' : ''}`}
                        data-testid="voice-input-btn"
                      >
                        {isListening ? (
                          <MicOff className="w-5 h-5" />
                        ) : (
                          <Mic className="w-5 h-5" />
                        )}
                      </Button>

                      {/* Text Input */}
                      <Input
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={activeModes.length > 0 
                          ? `Ask anything in ${activeModes.map(id => LEARN_ASSIST_MODES[id]?.shortName).join(' + ')} mode...`
                          : "Type your question or try 'feynman mode'..."
                        }
                        disabled={isLoading || !hasApiKey}
                        className="h-11 min-w-0 flex-1 rounded-xl"
                        data-testid="chat-input"
                      />

                      {/* Send Button */}
                      <Button
                        onClick={sendMessage}
                        disabled={!inputText.trim() || isLoading || !hasApiKey}
                        className={`flex-shrink-0 rounded-xl ${
                          activeModes.length > 0 
                            ? blendModeColors(activeModes) + ' text-white border-0'
                            : ''
                        }`}
                        data-testid="send-btn"
                      >
                        {isLoading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : activeModes.length > 0 ? (
                          <Zap className="w-5 h-5" />
                        ) : (
                          <Send className="w-5 h-5" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    {activeModes.length > 0 
                      ? `💡 Tip: Type "exit mode" or use the × buttons to deactivate modes`
                      : voiceMode === 'browser' 
                        ? 'Using browser speech recognition (free)'
                        : 'Using OpenAI Whisper for transcription'}
                    {speakResponses && ` • ${speechProvider === 'xai' ? `Grok voice: ${xaiVoiceId}` : 'Browser speech output'}`}
                    {hasContext && ' • Context attached'}
                  </p>
                </div>
              </div>
            </>
        </div>

        {/* Context Panel */}
        {showContextPanel && (
          <div className="fixed inset-x-0 bottom-0 top-14 z-40 overflow-y-auto border-t border-white/20 bg-background/95 p-4 shadow-2xl backdrop-blur-xl md:inset-x-auto md:right-0 md:top-16 md:w-80 md:border-l md:border-t-0 md:bg-background/70">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-serif text-lg">Context Sources</h3>
              <Button variant="ghost" size="sm" className="md:hidden" onClick={() => setShowContextPanel(false)}>
                Close
              </Button>
            </div>

            {/* Enhanced Multi-Lecture Selection */}
            <div className="mb-6">
              <Label className="text-sm text-muted-foreground mb-2 block">Lecture Context</Label>
              <Popover open={lecturePickerOpen} onOpenChange={setLecturePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={lecturePickerOpen}
                    className="w-full justify-between h-auto min-h-10 py-2"
                    data-testid="lecture-context-select"
                  >
                    <div className="flex flex-wrap gap-1 text-left">
                      {selectedLectures.length === 0 ? (
                        <span className="text-muted-foreground">Select lectures...</span>
                      ) : selectedLectures.length <= 2 ? (
                        getSelectedLecturesData().map(l => (
                          <Badge key={l.id} variant="secondary" className="text-xs">
                            {l.title.substring(0, 15)}...
                          </Badge>
                        ))
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          {selectedLectures.length} lectures selected
                        </Badge>
                      )}
                    </div>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[min(20rem,calc(100vw-2rem))] p-0" align="start">
                  <div className="p-2 border-b">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Select Lectures</span>
                      {selectedLectures.length > 0 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setSelectedLectures([])}
                          className="h-7 text-xs"
                        >
                          Clear all
                        </Button>
                      )}
                    </div>
                  </div>
                  <ScrollArea className="max-h-80">
                    <div className="p-2 space-y-1">
                      {lectures.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">
                          No completed lectures yet
                        </p>
                      ) : (
                        lectures.map((lecture) => {
                          const subject = getSubjectForLecture(lecture);
                          const isSelected = selectedLectures.includes(lecture.id);
                          
                          return (
                            <div
                              key={lecture.id}
                              onClick={() => toggleLectureSelection(lecture.id)}
                              className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                isSelected ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted'
                              }`}
                            >
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleLectureSelection(lecture.id)}
                                className="mt-0.5"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {lecture.title}
                                </p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  {subject && (
                                    <Badge 
                                      variant="outline" 
                                      className="text-[10px] px-1.5 py-0"
                                      style={{ 
                                        borderColor: subject.color,
                                        color: subject.color
                                      }}
                                    >
                                      <BookOpen className="w-2.5 h-2.5 mr-1" />
                                      {subject.name}
                                    </Badge>
                                  )}
                                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                    <Calendar className="w-2.5 h-2.5" />
                                    {formatDate(lecture.created_at)}
                                  </span>
                                  {lecture.audio_duration && (
                                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                      <Clock className="w-2.5 h-2.5" />
                                      {formatDuration(lecture.audio_duration)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {isSelected && (
                                <Check className="w-4 h-4 text-primary flex-shrink-0" />
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </ScrollArea>
                  {selectedLectures.length > 0 && (
                    <div className="p-2 border-t bg-muted/50">
                      <p className="text-xs text-muted-foreground">
                        {selectedLectures.length} lecture{selectedLectures.length !== 1 ? 's' : ''} will provide context for your questions
                      </p>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
              
              {/* Selected lectures quick view */}
              {selectedLectures.length > 0 && (
                <div className="mt-2 space-y-1">
                  {getSelectedLecturesData().map((lecture) => {
                    const subject = getSubjectForLecture(lecture);
                    return (
                      <div 
                        key={lecture.id}
                        className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg text-xs"
                      >
                        <BookOpen className="w-3 h-3 text-muted-foreground" />
                        <span className="flex-1 truncate">{lecture.title}</span>
                        {subject && (
                          <span 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: subject.color }}
                          />
                        )}
                        <button 
                          onClick={() => toggleLectureSelection(lecture.id)} 
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Document Upload */}
            <div className="mb-6">
              <Label className="text-sm text-muted-foreground mb-2 block">Upload to Library</Label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".txt,.pdf,.md"
                className="hidden"
              />
              <Button
                variant="outline"
                className="w-full rounded-xl h-10"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingDoc}
                data-testid="upload-doc-btn"
              >
                {isUploadingDoc ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Save File And Attach
              </Button>
              
              {/* Uploaded Documents */}
              {contextDocuments.length > 0 && (
                <div className="mt-2 space-y-2">
                  {contextDocuments.map((doc, index) => (
                    <LectureAiChip
                      key={index}
                      icon={FileText}
                      label={doc.name}
                      tone="document"
                      onRemove={() => removeDocument(index)}
                      className="w-full justify-start rounded-2xl"
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="mb-6">
              <div className="mb-2 flex items-center justify-between">
                <Label className="text-sm text-muted-foreground">Attach From Library</Label>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={loadLibraryDocuments}>
                  Refresh
                </Button>
              </div>
              <div className="space-y-2 rounded-xl border border-border/60 bg-background/70 p-2">
                {libraryDocuments.length === 0 ? (
                  <p className="px-2 py-3 text-sm text-muted-foreground">
                    No saved documents yet.
                  </p>
                ) : (
                  libraryDocuments.slice(0, 6).map((document) => {
                    const attached = contextDocuments.some((item) => item.documentId === document.id);
                    const canAttach = document.has_extracted_text || document.status === 'upload_complete';
                    return (
                      <div key={document.id} className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm hover:bg-muted/60">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-foreground">{document.title}</p>
                          <p className="truncate text-xs text-muted-foreground">{document.original_filename}</p>
                        </div>
                        <Button
                          type="button"
                          variant={attached ? 'secondary' : 'outline'}
                          size="sm"
                          className="h-8"
                          disabled={attached || !canAttach}
                          onClick={() => attachLibraryDocument(document)}
                        >
                          {attached ? 'Attached' : canAttach ? 'Attach' : 'Extract first'}
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* URL Input */}
            <div className="mb-6">
              <Label className="text-sm text-muted-foreground mb-2 block">Save Website To Library</Label>
              <div className="flex gap-2">
                <Input
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://..."
                  className="h-10 rounded-xl"
                  data-testid="url-input"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddUrl()}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleAddUrl}
                  disabled={!urlInput.trim() || isFetchingUrl}
                  className="rounded-xl h-10 w-10"
                  data-testid="add-url-btn"
                >
                  {isFetchingUrl ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <LinkIcon className="w-4 h-4" />
                  )}
                </Button>
              </div>
              
              {/* Added URLs */}
              {contextUrls.length > 0 && (
                <div className="mt-2 space-y-2">
                  {contextUrls.map((urlObj, index) => (
                    <LectureAiChip
                      key={index}
                      icon={Globe}
                      label={urlObj.url}
                      tone="link"
                      onRemove={() => removeUrl(index)}
                      className="w-full justify-start rounded-2xl"
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Context Summary */}
            {hasContext && (
              <div className="p-3 bg-primary/10 rounded-xl">
                <p className="text-sm text-muted-foreground">
                  <strong>Active context:</strong>
                </p>
                <ul className="text-sm text-muted-foreground mt-1 space-y-0.5">
                  {selectedLectures.length > 0 && (
                    <li className="flex items-center gap-1">
                      <BookOpen className="w-3 h-3" />
                      {selectedLectures.length} Lecture{selectedLectures.length !== 1 ? 's' : ''}
                    </li>
                  )}
                  {contextDocuments.length > 0 && (
                    <li className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      {contextDocuments.length} Document{contextDocuments.length !== 1 ? 's' : ''}
                    </li>
                  )}
                  {contextUrls.length > 0 && (
                    <li className="flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      {contextUrls.length} URL{contextUrls.length !== 1 ? 's' : ''}
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </main>
      </div>
    </div>
  );
}
